import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { MagnusHealth, MagnusAlerts, MagnusCallerId, SipHistory } from "@/components/MagnusPanel";
import { Stack, CheckCircle, Key, CaretDown, CreditCard, LinkBreak, Eye, EyeSlash, Sparkle, Gift } from "@phosphor-icons/react";

function CredentialForm({ provider, onSaved }) {
  const [values, setValues] = useState({});
  const [show, setShow] = useState({});
  const [busy, setBusy] = useState(false);
  const fields = provider.cred_fields || [];

  const save = async () => {
    const payload = Object.fromEntries(Object.entries(values).filter(([, v]) => (v || "").trim()));
    if (Object.keys(payload).length === 0) { toast.error("Enter at least one value"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/providers/${provider.key}/credentials`, { fields: payload });
      toast.success(`${provider.name} credentials saved`);
      setValues({}); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 space-y-2 border-t border-white/10 pt-4" data-testid={`creds-${provider.key}`}>
      {fields.map((f) => {
        const isEnv = f.endsWith("_ENV");
        const isPct = f.endsWith("_PCT");
        return (
          <div key={f}>
            <label className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span>{f}{isEnv ? " · sandbox / production" : isPct ? " · margin %" : ""}</span>
              {provider.configured?.[f]
                ? <span className="text-emerald-400">set · {provider.masked?.[f]}</span>
                : <span className="text-amber-400">not set</span>}
            </label>
            {isEnv ? (
              <select data-testid={`cred-input-${provider.key}-${f}`}
                value={values[f] ?? (provider.mode === "LIVE" ? "production" : "sandbox")}
                onChange={(e) => setValues({ ...values, [f]: e.target.value })}
                className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-xs outline-none focus:border-primary">
                <option value="sandbox">sandbox (testing)</option>
                <option value="production">production (live)</option>
              </select>
            ) : isPct ? (
              <input data-testid={`cred-input-${provider.key}-${f}`} value={values[f] ?? ""} type="number" min="0" autoComplete="off"
                onChange={(e) => setValues({ ...values, [f]: e.target.value })}
                placeholder={provider.configured?.[f] ? "leave blank to keep" : "0"}
                className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 font-mono-metric text-xs outline-none focus:border-primary" />
            ) : (
              <div className="relative mt-1">
                <input data-testid={`cred-input-${provider.key}-${f}`} value={values[f] ?? ""} type={show[f] ? "text" : "password"} autoComplete="off"
                  onChange={(e) => setValues({ ...values, [f]: e.target.value })}
                  placeholder={provider.configured?.[f] ? "•••• (leave blank to keep)" : "Paste value"}
                  className="w-full rounded-lg border border-white/10 bg-background px-3 py-2 pr-9 font-mono-metric text-xs outline-none focus:border-primary" />
                <button type="button" data-testid={`cred-eye-${provider.key}-${f}`} onClick={() => setShow((x) => ({ ...x, [f]: !x[f] }))}
                  className="absolute inset-y-0 right-0 flex items-center px-2.5 text-muted-foreground hover:text-foreground">
                  {show[f] ? <EyeSlash size={15} /> : <Eye size={15} />}
                </button>
              </div>
            )}
          </div>
        );
      })}
      <button data-testid={`save-creds-${provider.key}`} onClick={save} disabled={busy}
        className="mt-2 flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-50">
        <Key size={14} weight="bold" /> {busy ? "Saving…" : "Save credentials"}
      </button>
    </div>
  );
}

function DisconnectButton({ providerKey, name, onDone }) {
  const [busy, setBusy] = useState(false);
  const disconnect = async () => {
    if (!window.confirm(`Disconnect ${name}? This clears the stored credentials.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/providers/${providerKey}/credentials`);
      toast.success(`${name} disconnected`); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <button data-testid={`disconnect-${providerKey}`} onClick={disconnect} disabled={busy}
      className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-red-400 hover:text-red-300 disabled:opacity-50">
      <LinkBreak size={14} weight="bold" /> {busy ? "Disconnecting…" : "Disconnect"}
    </button>
  );
}

function ProviderTestButton({ provider }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const test = async () => {
    setBusy(true); setState(null);
    try {
      const r = (await api.post(`/admin/${provider.key}/test`)).data;
      setState(r);
      r.ok ? toast.success(`${provider.name} connection OK`) : toast.error(r.detail || "Connection failed");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3">
      <button data-testid={`test-conn-${provider.key}`} onClick={test} disabled={busy}
        className="flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary disabled:opacity-50">
        <Sparkle size={13} weight="bold" /> {busy ? "Testing…" : "Test connection"}
      </button>
      {state && (
        <p data-testid={`test-conn-result-${provider.key}`} className={`mt-2 text-xs ${state.ok ? "text-emerald-400" : "text-red-400"}`}>
          {state.ok ? `✓ ${state.detail}` : `✕ ${state.detail}`}
        </p>
      )}
    </div>
  );
}

function AiTestButton() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const test = async () => {
    setBusy(true); setState(null);
    try {
      const r = (await api.post("/voice-book/selftest")).data;
      setState(r);
      r.ok ? toast.success("OpenAI Realtime connected") : toast.error(r.detail || "Connection failed");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3">
      <button data-testid="ai-voice-selftest" onClick={test} disabled={busy}
        className="flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary disabled:opacity-50">
        <Sparkle size={13} weight="bold" /> {busy ? "Testing…" : "Test connection"}
      </button>
      {state && (
        <p data-testid="ai-voice-selftest-result" className={`mt-2 text-xs ${state.ok ? "text-emerald-400" : "text-red-400"}`}>
          {state.ok ? `✓ ${state.detail}` : `✕ ${state.detail}`}
        </p>
      )}
    </div>
  );
}

export default function AdminProviders() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["providers"], queryFn: async () => (await api.get("/providers")).data });
  const providers = data?.providers || [];
  const payments = data?.payment_providers || [];
  const ai = data?.ai_providers || [];
  const commerce = data?.commerce_providers || [];
  const [open, setOpen] = useState(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["providers"] });

  const ProviderCard = ({ p, icon: Icon }) => {
    const anySet = Object.values(p.configured || {}).some(Boolean);
    const isPayment = p.kind === "payment";
    const badge = isPayment
      ? (p.connected ? (p.mode === "LIVE" ? "LIVE" : "SANDBOX") : "DISCONNECTED")
      : (anySet ? "Connected" : p.mode);
    const badgeCls = (isPayment && p.mode === "LIVE") || (!isPayment && anySet)
      ? "bg-emerald-500/15 text-emerald-400"
      : isPayment && p.connected ? "bg-amber-500/15 text-amber-400" : "bg-white/10 text-muted-foreground";
    return (
      <div data-testid={`provider-${p.key}`} className="rounded-lg border border-white/10 bg-card p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary"><Icon size={20} weight="bold" /></div>
            <div><p className="font-display font-semibold">{p.name}</p><p className="font-mono-metric text-xs text-muted-foreground">{p.key}</p></div>
          </div>
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${badgeCls}`}>{badge}</span>
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {(p.services || []).map((s) => (
            <span key={s} className="rounded-full bg-white/5 px-2.5 py-0.5 text-[10px] font-medium capitalize text-muted-foreground">{s}</span>
          ))}
        </div>
        {(p.cred_fields || []).length > 0 && (
          <button data-testid={`toggle-creds-${p.key}`} onClick={() => setOpen(open === p.key ? null : p.key)}
            className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-primary">
            <Key size={14} weight="bold" /> {anySet ? "Update credentials" : "Add credentials"}
            <CaretDown size={12} className={`transition-transform ${open === p.key ? "rotate-180" : ""}`} />
          </button>
        )}
        {anySet && !isPayment && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-400">
            <CheckCircle size={14} weight="fill" /> Adapter connected
          </div>
        )}
        {p.kind === "ai" && <AiTestButton />}
        {p.key === "magnus" && <><MagnusHealth /><SipHistory /><MagnusAlerts /><MagnusCallerId /></>}
        {(p.key === "reloadly" || p.key === "flutterwave" || p.key === "stripe") && <ProviderTestButton provider={p} />}
        {open === p.key && (
          <>
            <CredentialForm provider={p} onSaved={refresh} />
            {anySet && <DisconnectButton providerKey={p.key} name={p.name} onDone={() => { setOpen(null); refresh(); }} />}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Providers</h1>
        <p className="mt-1 text-sm text-muted-foreground">Add or update carrier &amp; payment credentials. Paste API keys to activate a provider — everything stays server-side.</p>
      </div>

      {payments.length > 0 && (
        <div>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground"><CreditCard size={16} /> Payment providers</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {payments.map((p) => <ProviderCard key={p.key} p={p} icon={CreditCard} />)}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">For live <span className="font-mono-metric">Stripe</span> (your own account), paste your <span className="font-mono-metric">STRIPE_SECRET_KEY</span> (<span className="font-mono-metric">sk_live_…</span>), <span className="font-mono-metric">STRIPE_PUBLISHABLE_KEY</span> (<span className="font-mono-metric">pk_live_…</span>) and the webhook signing secret <span className="font-mono-metric">STRIPE_WEBHOOK_SECRET</span>, then hit Test connection. Also add the same keys in the production Secrets panel and re-publish.</p>
          <p className="mt-2 text-xs text-muted-foreground">For live PayPal, paste your Live app <span className="font-mono-metric">PAYPAL_CLIENT_ID</span> + <span className="font-mono-metric">PAYPAL_SECRET</span>. Leave <span className="font-mono-metric">PAYPAL_BASE</span> blank for live, or set <span className="font-mono-metric">https://api-m.sandbox.paypal.com</span> for testing.</p>
        </div>
      )}

      {commerce.length > 0 && (
        <div>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground"><Gift size={16} /> Airtime, data &amp; gift cards</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {commerce.map((p) => <ProviderCard key={p.key} p={p} icon={Gift} />)}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Paste your <span className="font-mono-metric">RELOADLY_CLIENT_ID</span> + <span className="font-mono-metric">RELOADLY_CLIENT_SECRET</span>. Set <span className="font-mono-metric">RELOADLY_ENV</span> to <span className="font-mono-metric">sandbox</span> or <span className="font-mono-metric">production</span>. Margins (optional) are whole-number percentages.</p>
        </div>
      )}

      {ai.length > 0 && (
        <div>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground"><Sparkle size={16} /> SI services</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {ai.map((p) => <ProviderCard key={p.key} p={p} icon={Sparkle} />)}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Paste your own <span className="font-mono-metric">OPENAI_API_KEY</span> (a Realtime-enabled key) to power the SI phone receptionist &amp; voice booking. Pay-as-you-go on your OpenAI account — stored encrypted server-side, never shown again.</p>
        </div>
      )}

      <div>
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground"><Stack size={16} /> Carriers &amp; services</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {providers.map((p) => <ProviderCard key={p.key} p={p} icon={Stack} />)}
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-card p-5 text-sm text-muted-foreground">
        <p className="font-semibold text-foreground">Generic contract</p>
        <p className="mt-2">Every adapter implements: <span className="font-mono-metric text-xs">search_inventory · get_rate · purchase · provision · activate · route · send · get_status · get_balance · get_usage · cancel · webhook</span>. Provider credentials remain server-side only.</p>
      </div>
    </div>
  );
}
